import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import { setHasBeenRan } from '../store/project/projectslice';
import { logDebug } from '../../utils/utils';
import { LogTag } from '../../config/loggingConfig';
import { DynamicFormikProps } from '../../types/FormTypes';
import { useDebounce } from '../../hooks/useDebounce';

/**
 * Hook personalizzato per gestire gli effetti collaterali del form
 * 🚀 REFACTORED: Now uses State Machine Pattern for cleaner logic
 */
export const useFormEffects = (
    canRedirect: boolean,
    shouldAutoSubmit: boolean,
    projectHasBeenRan: boolean | undefined,
    formikRef: React.RefObject<DynamicFormikProps>,
    isApplyingDefaults = false
) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmittingDesign, setIsSubmittingDesign] = useState(false);

    // Read navigation coordination flag from Redux
    // This is set during Execute button calculation and cleared after 500ms
    const isNavigatingToResults = useAppSelector(state => state.Dynamic.project.data?.isNavigatingToResults ?? false);

    // Create debounced function for design changes
    const debouncedDesignSubmit = useDebounce(() => {
        logDebug('useFormEffects', 'Design sections changed - triggering results recalculation without resetting hasBeenRan');

        if (formikRef.current) {
            setIsSubmittingDesign(true);

            // Submit del form per aggiornare i dati del progetto e triggerare il ricalcolo dei risultati
            formikRef.current.submitForm().finally(() => {
                // Reset del flag dopo che il submit è completato
                setTimeout(() => setIsSubmittingDesign(false), 100);
            });
        }
    }, 500); // Debounce di 500ms

    // 🚀 SIMPLIFIED: State machine now handles redirect logic entirely
    // We don't need to reset hasBeenRan here - the state machine's canRedirect flag is sufficient
    // hasBeenRan should only be reset when:
    // 1. User makes NON-design changes (state machine → FORM_DIRTY → canRedirect=true)
    // 2. Form submission completes (handled by orchestrator)
    //
    // Design-only changes (state machine → DESIGN_DIRTY → canRedirect=false) should NEVER reset hasBeenRan
    useEffect(() => {
        console.log('🔍 useFormEffects: Checking form state', {
            canRedirect,
            shouldAutoSubmit,
            projectHasBeenRan,
            isApplyingDefaults,
            isNavigatingToResults,
            currentPath: location.pathname,
        });

        // 🔧 FIX: Only reset hasBeenRan if user makes structural (non-design) changes
        // State machine sets canRedirect=true ONLY for FORM_DIRTY state (non-design changes)
        // State machine sets canRedirect=false for DESIGN_DIRTY state (design-only changes)
        // Also check isNavigatingToResults to avoid interfering with Execute button calculations
        //
        // 🔧 FIX (imperial_fix): If we're ALREADY on /results, ignore isNavigatingToResults flag
        // The flag is meant to prevent redirect DURING navigation to results, not AFTER arrival
        // If flag persists after arrival (due to timing/race conditions), ignore it
        const onResultsPage = location.pathname.includes('/results');
        const shouldBlockForNavigation = isNavigatingToResults && !onResultsPage;

        if (canRedirect && projectHasBeenRan === true && !isApplyingDefaults && !shouldBlockForNavigation) {
            console.log('🔴 useFormEffects: RESETTING hasBeenRan to false!', {
                reason: 'non-design structural changes detected (canRedirect=true from state machine)',
                canRedirect,
                projectHasBeenRan,
                isApplyingDefaults,
                isNavigatingToResults,
                shouldAutoSubmit,
            });
            dispatch(setHasBeenRan(false));
        } else if (projectHasBeenRan === true && !canRedirect) {
            console.log('✅ useFormEffects: hasBeenRan NOT reset - state machine canRedirect=false (clean or design-only state)');
        }
    }, [canRedirect, projectHasBeenRan, dispatch, isApplyingDefaults, shouldAutoSubmit, location.pathname, isNavigatingToResults]);

    // 🚀 RE-ENABLED: Auto-submit for design-only changes (slider modifications)
    // CONTEXT: Toggle enable flags use immediate Redux dispatch (updateDesignEnableFlags)
    // BUT slider values (baseThickness, surfaceThickness, etc.) REQUIRE backend calculation
    //
    // WHY IT'S SAFE NOW:
    // 1. Auto-submit uses state machine correctly (setSubmittingDesign → SUBMITTING → CLEAN)
    // 2. Recent fix (commit febaeb02) resets state machine after setHasBeenRan(true)
    // 3. State flow: SUBMITTING → CLEAN → canRedirect=false → NO unwanted redirect
    //
    // Canvas needs backend-calculated values to update, not just Formik state
    useEffect(() => {
        if (shouldAutoSubmit && projectHasBeenRan === true && !isSubmittingDesign) {
            debouncedDesignSubmit();
        }

        // Cleanup automatico fornito da useDebounce
        return () => {
            (debouncedDesignSubmit as any).cancel?.();
        };
    }, [shouldAutoSubmit, projectHasBeenRan, isSubmittingDesign, debouncedDesignSubmit]);

    // 🔧 ROBUST FIX: Effetto per navigazione automatica con coordinazione flag
    // Redirect a /designCondition quando hasBeenRan diventa false SOLO se non c'è navigazione programmata
    // 🔧 FIX: Also prevent redirect when shouldAutoSubmit=true (design-only changes like enableSurfaceDesign='yes')
    //
    // 🔧 FIX (imperial_fix): Removed isNavigatingToResults check from redirect condition.
    // REASON: If we're ALREADY on /results, the flag is irrelevant - navigation completed.
    // The flag should only block during the transition TO results, not after arrival.
    // Keeping it caused redirect to be blocked forever if flag persisted due to race conditions.
    useEffect(() => {
        console.log('🔍 useFormEffects: Redirect effect running', {
            pathname: location.pathname,
            projectHasBeenRan,
            isNavigatingToResults,
            onResultsPage: location.pathname.includes('/results'),
            shouldAutoSubmit,
            canRedirect,
        });

        // 🔧 FIX: Only redirect if ALL conditions are met (use canRedirect as primary gate)
        // NOTE: isNavigatingToResults NOT checked here - if we're on /results, navigation is complete
        if (location.pathname.includes('/results') && canRedirect && projectHasBeenRan === false) {
            // Estrai il projectId dall'URL
            const pathParts = location.pathname.split('/');
            const projectId = pathParts[pathParts.length - 1];

            if (projectId && projectId !== 'results') {
                console.log(`🔄 useFormEffects: REDIRECTING from results to designCondition for project ${projectId}`);
                navigate(`/designCondition/${projectId}`);
            }
        } else if (location.pathname.includes('/results') && !canRedirect) {
            console.log('✅ useFormEffects: Redirect PREVENTED - state machine canRedirect=false (design-only or clean state)');
        }
    }, [projectHasBeenRan, location.pathname, navigate, canRedirect]);

    return {
        isSubmittingDesign,
    };
};
